import { adminRightOptions, allManagementRightOptions, rightActivePrefixes, rightPrefixes, superAdminRightOptions, userKinds } from 'utils/constants/management-right-constants';
import _ from "lodash";

export default class HelperManagementRights
{
	static defaultRights = {};
	static userKinds = {};
	static userRights = {};
	static default_employee = null;
	static debug = false;

	static buildDefaultRights()
	{
		if( Object.keys( this.defaultRights ).length === 0 )
		{
			// create the default rights (set everything to inactive)
			Object.values( userKinds ).forEach( ( userKind ) => {
				this.defaultRights[ userKind ] = {
					[rightPrefixes.VIEW]: {},
					[rightPrefixes.EDIT]: {}
				};

				allManagementRightOptions.VIEW.forEach( (value) => {
					this.defaultRights[ userKind ][ rightPrefixes.VIEW ][ value ] = rightActivePrefixes.INACTIVE;
				});

				allManagementRightOptions.EDIT.forEach( (value) => {
					this.defaultRights[ userKind ][ rightPrefixes.EDIT ][ value ] = rightActivePrefixes.INACTIVE;
				});
			});

			// TODO below code could be cleaner/less lines
			adminRightOptions.VIEW.forEach( (value) => {
				this.defaultRights[ userKinds.ADMIN ][ rightPrefixes.VIEW ][ value ] = rightActivePrefixes.ACTIVE;
			});

			adminRightOptions.EDIT.forEach( (value) => {
				this.defaultRights[ userKinds.ADMIN ][ rightPrefixes.EDIT ][ value ] = rightActivePrefixes.ACTIVE;
			});

			// build the admin rights
			superAdminRightOptions.VIEW.forEach( (value) => {
				this.defaultRights[ userKinds.SUPER_ADMIN ][ rightPrefixes.VIEW ][ value ] = rightActivePrefixes.ACTIVE;
			});

			superAdminRightOptions.EDIT.forEach( (value) => {
				this.defaultRights[ userKinds.SUPER_ADMIN ][ rightPrefixes.EDIT ][ value ] = rightActivePrefixes.ACTIVE;
			});
		}
	}

	static setEmployee( employee, set_as_default = false ) {
		this.buildDefaultRights();

		if( set_as_default ) {
			this.default_employee = employee;
		}

		const employee_guid = this.get_employee_guid( employee );

		const kind = _.get( employee, ['user', 'kind' ], userKinds.UNKNOWN );
		this.userKinds[ employee_guid ] = kind === 'superadmin' ? userKinds.SUPER_ADMIN : kind;
		if( !this.defaultRights[ this.userKinds[ employee_guid ] ] ) {
			this.userKinds[ employee_guid ] = userKinds.UNKNOWN;
		}
		
		// cloning needs to be done quick and dirty to prevent by reference
		this.userRights[ employee_guid ] = JSON.parse(JSON.stringify( this.defaultRights[ this.userKinds[ employee_guid ] ] ));

		const userRights = _.get( employee, [ 'user', 'rights' ], [] );
		if( userRights.length > 0 ) {
			// Set all rights to no for good measure (if the user had no rights he/she has the defaults enabled)
			// New rights are in that case unavailable until applied
			[ rightPrefixes.VIEW, rightPrefixes.EDIT ].forEach( type => {
				Object.keys( this.userRights[ employee_guid ][ type ] ).forEach( what => {
					this.userRights[ employee_guid ][ type ][ what ] = rightActivePrefixes.INACTIVE;
				});
			});

			userRights.forEach( user_right => {
				if( user_right.user_right.indexOf( 'view_' ) !== -1 ) {
					this.userRights[ employee_guid ][ rightPrefixes.VIEW ][ user_right.user_right ] = user_right.active;
				} else {
					this.userRights[ employee_guid ][ rightPrefixes.EDIT ][ user_right.user_right ] = user_right.active;
				}
			});
		}
	}

	static hasViewRight( what, employee = null ) {
		return this.hasRight( rightPrefixes.VIEW, what, employee );
	}

	static hasEditRight( what, employee = null ) {
		return this.hasRight( rightPrefixes.EDIT, what, employee );
	}

	static hasRight( group, what, employee ) {
		this.buildDefaultRights();

		const employeeGuid = this.get_employee_guid( employee );
		if( _.get( this.userKinds, employeeGuid, userKinds.UNKNOWN ) === userKinds.UNKNOWN ) {
			return false;
		}

		const status = _.get( this.userRights, [ employeeGuid, group, what ], rightActivePrefixes.INACTIVE );

		return status === rightActivePrefixes.ACTIVE;
	}

	static setViewRight( what, state, employee = null ) {
		this.setRight( rightPrefixes.VIEW, what, state, employee );
	}

	static setEditRight( what, state, employee = null ) {
		this.setRight( rightPrefixes.EDIT, what, state, employee );
	}

	static setRight( group, what, state, employee = null ) {
		this.buildDefaultRights();

		let employee_guid = this.get_employee_guid( employee );

		this.userRights[ employee_guid ][ group ][ what ] = state;
	}

	static canManageAdmin( employee ) {
		const actorGuid = this.get_employee_guid();
		if( !actorGuid || actorGuid === this.get_employee_guid(employee) || !this.hasViewRight('view_employees') || !this.hasEditRight('edit_employees') ) {
			return false;
		}
		const kind = employee?.user?.kind;
		return !['superadmin', 'super_admin'].includes(kind) || this.userKinds[actorGuid] === userKinds.SUPER_ADMIN;
	}

	static canCreateAdmin( employee ) {
		return this.canManageAdmin(employee) && [...adminRightOptions.VIEW, ...adminRightOptions.EDIT].every( ( right ) => {
			return this.hasRight(right.startsWith('view_') ? rightPrefixes.VIEW : rightPrefixes.EDIT, right);
		});
	}

	static getAllRightsFlat( employee = null ) {
		let employee_guid = this.get_employee_guid( employee );

		let flat_rights = [];

		[ rightPrefixes.VIEW, rightPrefixes.EDIT ].forEach( type => {
			Object.keys( this.userRights[ employee_guid ][ type ] ).forEach( what => {
				flat_rights.push( { id: what, active: this.userRights[ employee_guid ][ type ][ what ] } );
			});
		});

		return flat_rights;
	}

	static get_employee_guid( employee = null ) {
		if( employee === null ) {
			employee = this.default_employee;
		}

		return  (_.get( employee, 'guid' ) ?? '').replaceAll('-','');
	}
}
